import React from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { budgetLabel, fmtDate, type AiSuggestion } from "../campanhasApi";

type Props = {
  suggestion: AiSuggestion;
  /** Anúncios da campanha anterior marcados para reaproveitar (com a imagem). */
  keepReuse: string[];
  /** Sem ele (rascunho já salvo), a lista só informa: os anúncios já foram copiados. */
  onToggleReuse?: (adId: string) => void;
};

/** A sugestão da IA, explicada, para a pessoa revisar antes de criar o rascunho. */
const SuggestionReview: React.FC<Props> = ({ suggestion: s, keepReuse, onToggleReuse }) => {
  const c = s.campaign;
  const period = c.endDate ? `${fmtDate(c.startDate)} a ${fmtDate(c.endDate)}` : `a partir de ${fmtDate(c.startDate)}`;
  return (
    <div className="aiReview">
      <section className="ecoCard cmpPanel aiReview__explain">
        <h2 className="cmpPanel__title">
          <EcoIcon name="helpCircle" size={18} /> Por que esta campanha
        </h2>
        <p className="aiText">{s.explanation}</p>
        <h3 className="aiReview__subtitle">Como rodar o teste</h3>
        <p className="aiText">{s.testPlan}</p>
        {s.warnings.length ? (
          <div className="cmpNotice">
            <EcoIcon name="alertTriangle" size={16} />
            <ul className="aiList">
              {s.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="ecoCard cmpPanel">
        <h2 className="cmpPanel__title">A campanha sugerida</h2>
        <dl className="cmpDl">
          <dt>Nome</dt>
          <dd>{c.name}</dd>
          <dt>Título da vaga</dt>
          <dd>{c.jobTitle}</dd>
          <dt>Descrição</dt>
          <dd style={{ whiteSpace: "pre-line" }}>{c.jobDescription}</dd>
          <dt>Perguntas extras</dt>
          <dd>{c.questions.length ? c.questions.map((q) => `${q.label}${q.required ? " (obrigatória)" : ""}`).join(" · ") : "Nenhuma"}</dd>
          <dt>Currículo</dt>
          <dd>{c.resumeRequired ? "Obrigatório" : "Opcional"}</dd>
          <dt>Locais</dt>
          <dd>
            {[
              ...c.storeLocations.map((l) => `${l.label} (loja, ${l.radiusKm} km)`),
              ...(c.points ?? []).map((x) => `${x.label} (${x.radiusKm} km a partir daqui)`),
              ...c.cities.map((x) => `${x.label} (${x.radiusKm} km)`),
            ].join(" · ") ||
              "Nenhum: inclua no formulário depois de criar"}
          </dd>
          <dt>Categoria Emprego</dt>
          <dd>
            {c.employmentCategory === true ? "Marcada" : "Não marcada (padrão da empresa)"}
          </dd>
          <dt>Orçamento</dt>
          <dd>{budgetLabel(c.budgetType, Math.round(c.budget * 100))}</dd>
          <dt>Período</dt>
          <dd>{period}</dd>
        </dl>
      </section>

      {s.reuseAds?.length ? (
        <section className="ecoCard cmpPanel">
          <h2 className="cmpPanel__title">Anúncios da campanha anterior que continuam</h2>
          <p className="cmpHint">
            {onToggleReuse
              ? "Vão para o rascunho com a mesma imagem. Desmarque o que não quiser levar."
              : "Já foram copiados para o rascunho, com a mesma imagem. Exclua no rascunho o que não quiser."}
          </p>
          {s.reuseAds.map((ad) => (
            <label key={ad.id} className="aiAdOption">
              {onToggleReuse ? <input type="checkbox" checked={keepReuse.includes(ad.id)} onChange={() => onToggleReuse(ad.id)} /> : null}
              <span>
                <strong>
                  Anúncio {ad.position}: {ad.headline}
                </strong>
                <span className="cmpSub">{ad.primaryText}</span>
              </span>
            </label>
          ))}
        </section>
      ) : null}

      <section className="ecoCard cmpPanel">
        <h2 className="cmpPanel__title">{s.reuseAds?.length ? "Textos novos sugeridos" : "Textos de anúncio sugeridos"}</h2>
        <p className="cmpHint">
          Depois de criar o rascunho, inclua cada anúncio com uma imagem e escolha um destes textos (eles aparecem prontos no formulário do anúncio).
        </p>
        <div className="aiAds">
          {s.ads.map((ad, i) => (
            <article key={i} className="aiAd">
              {ad.angle ? <span className="aiAd__angle">{ad.angle}</span> : null}
              <p className="aiText">{ad.primaryText}</p>
              <strong>{ad.headline}</strong>
              {ad.description ? <span className="cmpSub">{ad.description}</span> : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SuggestionReview;
